/**
 * 2D camera for Real/Imaginary modes.
 *
 * Provides a view-projection matrix (3x3) for transforming world
 * coordinates to clip space. Supports smooth follow and shake.
 */

import type { Vec2 } from '@/types';

export class Camera2D {
  /** Camera center in world coordinates */
  position: Vec2 = { x: 0, y: 0 };

  /** Viewport size in world units */
  viewWidth = 800;
  viewHeight = 450;

  /** Offset for look-ahead (camera leads the player) */
  leadOffset: Vec2 = { x: 200, y: 0 };

  /** Screen shake state */
  private shakeIntensity = 0;
  private shakeDecay = 5.0;
  private shakeOffset: Vec2 = { x: 0, y: 0 };

  /** Follow a target position smoothly */
  follow(target: Vec2, dt: number, smoothing = 8.0): void {
    const tx = target.x + this.leadOffset.x;
    const ty = target.y + this.leadOffset.y;
    this.position.x += (tx - this.position.x) * Math.min(1, smoothing * dt);
    this.position.y += (ty - this.position.y) * Math.min(1, smoothing * dt);
  }

  /** Trigger screen shake */
  shake(intensity: number): void {
    this.shakeIntensity = Math.max(this.shakeIntensity, intensity);
  }

  /** Update shake decay */
  update(dt: number): void {
    if (this.shakeIntensity > 0.01) {
      this.shakeOffset.x = (Math.random() * 2 - 1) * this.shakeIntensity;
      this.shakeOffset.y = (Math.random() * 2 - 1) * this.shakeIntensity;
      this.shakeIntensity *= Math.exp(-this.shakeDecay * dt);
    } else {
      this.shakeIntensity = 0;
      this.shakeOffset.x = 0;
      this.shakeOffset.y = 0;
    }
  }

  /** Resize viewport (call on canvas resize) */
  resize(width: number, height: number): void {
    // Maintain aspect ratio, fix height, adjust width
    const aspect = width / height;
    this.viewHeight = 450;
    this.viewWidth = this.viewHeight * aspect;
  }

  /**
   * Returns a 3x3 view-projection matrix (column-major, flat array)
   * that maps world coordinates to clip space [-1, 1].
   */
  getViewProjectionMatrix(): Float32Array {
    const cx = this.position.x + this.shakeOffset.x;
    const cy = this.position.y + this.shakeOffset.y;

    const sx = 2 / this.viewWidth;
    const sy = 2 / this.viewHeight;

    // Column-major 3x3:
    // [ sx,  0, 0 ]
    // [ 0,  sy, 0 ]
    // [ tx, ty, 1 ]
    return new Float32Array([
      sx,
      0,
      0,
      0,
      sy,
      0,
      -cx * sx,
      -cy * sy,
      1,
    ]);
  }
}
